import styled from 'styled-components';
import useSWRImmutable from 'swr/immutable';

import { IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { requestService } from 'shared/utils/services/requestService';
import { getWindowItemProductVariantRowPriceLength } from 'productTemplate/utils/getWindowItemProductVariantRowPriceLength';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { WindowItemProductVariantRow } from 'shared/components/Window';
import { t } from 'localization/utils/t';

const TP = 'shared.components.Window.WindowItemPRoductVariantRows';

const fetchData = (url) => requestService({ url, method: 'GET' });

interface IWindowItemProductVariantRowsProps {
  bestPriceVariant?: IBuyBarProductVariant;
  instantShipVariant?: IBuyBarProductVariant;
  newWithBoxIssuesVariant?: IBuyBarProductVariant;
  showNewWithBoxIssues?: boolean;
}

export const WindowItemProductVariantRows = ({
  bestPriceVariant,
  instantShipVariant,
  newWithBoxIssuesVariant,
  showNewWithBoxIssues = false,
}: IWindowItemProductVariantRowsProps) => {
  const { id, isFashionProduct, localizedSpecialDisplayPriceCents, productType } = useStoreSelector(
    (state) => state.pdp.productTemplate,
  );
  const isSneaker = productType === 'sneakers';
  const { data: descriptionCopy } = useSWRImmutable(
    `/web-api/v1/product_variants/buy_bar_copies?productTemplateId=${id}`,
    fetchData,
  );

  const instantShipPriceIsLowestPrice =
    bestPriceVariant?.lowestPriceCents?.amount ===
    instantShipVariant?.instantShipLowestPriceCents?.amount;
  const isUnderRetail =
    bestPriceVariant?.lowestPriceCents?.amount < localizedSpecialDisplayPriceCents?.amount;

  // calculates the width of the price div according to length
  const productPriceLength = getWindowItemProductVariantRowPriceLength(
    bestPriceVariant?.lowestPriceCents,
  );
  const instantShipPriceLength = getWindowItemProductVariantRowPriceLength(
    instantShipVariant?.instantShipLowestPriceCents,
  );
  const newWithDefectsPriceLength = getWindowItemProductVariantRowPriceLength(
    newWithBoxIssuesVariant?.lowestPriceCents,
  );
  const longestPriceLength = Math.max(
    productPriceLength,
    instantShipPriceLength,
    newWithDefectsPriceLength,
  );

  const getCondition = () => {
    if (isUnderRetail) {
      return isSneaker ? t(`${TP}.underRetail`, 'Under Retail') : t(`${TP}.sale`, 'Sale');
    }
    return t(`${TP}.bestPrice`, 'Best Price');
  };

  /**
   * NOTE: formally `getBoxConditionCopy` however separating out to a util
   * function that returns i18next.t response causes the return function to be
   * undefined. therefore must define function within the component
   */
  const boxConditionCopy = () => {
    switch (newWithBoxIssuesVariant?.boxCondition) {
      case 'badly_damaged':
        return t(`${TP}.damagedBox`, 'New / Damaged Box');
      case 'good_condition':
        return t(`${TP}.goodCondition`, 'New / Good Condition');
      case 'missing_lid':
        return t(`${TP}.missingLid`, 'New / Missing Lid');
      case 'no_original_box':
        return t(`${TP}.noOriginalBox`, 'New / No Original Box');
      default:
        return null;
    }
  };

  return (
    <WindowItemProductVariantRows.Wrapper>
      {bestPriceVariant && !instantShipPriceIsLowestPrice && (
        <WindowItemProductVariantRow
          condition={getCondition()}
          description={!isFashionProduct && descriptionCopy?.web?.best}
          isBestPrice
          isUnderRetail={isUnderRetail}
          longestPriceLength={longestPriceLength}
          variant={bestPriceVariant}
        />
      )}
      {instantShipVariant && (
        <WindowItemProductVariantRow
          condition={t(`${TP}.instant`, 'Instant')}
          description={descriptionCopy?.web?.instant}
          isInstant
          isUnderRetail={isUnderRetail}
          longestPriceLength={longestPriceLength}
          variant={instantShipVariant}
        />
      )}
      {showNewWithBoxIssues && newWithBoxIssuesVariant && (
        <WindowItemProductVariantRow
          condition={boxConditionCopy()}
          description={descriptionCopy?.web?.best}
          isUnderRetail={isUnderRetail}
          longestPriceLength={longestPriceLength}
          variant={newWithBoxIssuesVariant}
        />
      )}
    </WindowItemProductVariantRows.Wrapper>
  );
};

WindowItemProductVariantRows.Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  a:last-child {
    margin-bottom: 0;
  }
`;
